import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from '../../utils/toast';
import {
  Globe,
  FileCheck,
  Clock,
  Save,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import OffboardingStepSkeleton from './OffboardingStepSkeleton';
import HorizontalStepper from '../../components/UI/HorizontalStepper';
import { StatusBadge } from '../../components/UI/StatusBadge';
import WithdrawalModal from '../../components/WithdrawalModal';
import useWithdrawal from '../../hooks/useWithdrawal';
import useOffboardingWorkflow from '../../hooks/useOffboardingWorkflow';
import { getOffboardingSteps, calculateProgressPercentage } from '../../config/offboardingSteps';
import { initializeStepData } from '../../utils/workflowUtils';


const OffboardingStep6 = () => {
  const { workflowId } = useParams();
  const navigate = useNavigate();
  const { handleWithdrawal } = useWithdrawal();
  const { workflow, loading, saving: hookSaving, saveStep } = useOffboardingWorkflow(workflowId);
  const [saving, setSaving] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [stepData, setStepData] = useState({
    requires_ir21: false,
    ir21_form_generated: false,
    ir21_submitted_date: '',
    work_permit_cancelled: false,
    exit_permit_arranged: false,
    ir21_reference_number: '',
    immigration_notes: '',
    compliance_checklist: {
      work_permit_verified: false,
      exit_permit_applied: false,
      embassy_notified: false,
      documents_collected: false,
      final_clearance_obtained: false
    }
  });

  // Define the 7 steps for offboarding overview
  // Handle step click navigation
  const handleStepClick = (step) => {
    navigate(step.route);
  };

  // Initialize step data when workflow loads
  useEffect(() => {
    if (workflow) {
      const initialData = initializeStepData(workflow, 6);
      setStepData(initialData);
    }
  }, [workflow]);

  const handleSave = async () => {
    try {
      setSaving(true);

      const saveData = {
        ...stepData,
        compliance_checklist: JSON.stringify(stepData.compliance_checklist)
      };

      await saveStep(6, saveData);
    } catch (error) {
      console.error('Error saving step:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleChecklistChange = (item, checked) => {
    setStepData(prev => ({
      ...prev,
      compliance_checklist: {
        ...prev.compliance_checklist,
        [item]: checked
      }
    }));
  };

  const handleNext = () => {
    navigate(`/admin/employee-lifecycle/offboarding/${workflowId}/step-7`);
  };

  const handlePrev = () => {
    navigate(`/admin/employee-lifecycle/offboarding/${workflowId}/step-5`);
  };

  const isChecklistComplete = () => {
    if (!stepData.requires_ir21) return true;
    const checklist = stepData.compliance_checklist;
    return checklist.work_permit_verified &&
           checklist.exit_permit_applied &&
           checklist.embassy_notified &&
           checklist.documents_collected &&
           checklist.final_clearance_obtained;
  };

  if (loading) {
    return <OffboardingStepSkeleton />;
  }

  if (!workflow) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Workflow Not Found</h2>
          <p className="text-gray-600">The requested offboarding workflow could not be found.</p>
        </div>
      </div>
    );
  }

  // Skip step if IR21 is not required
  if (!stepData.requires_ir21) {
    return (
      <div className="min-h-screen bg-gray-50 space-y-8">
        {/* Page Header */}
        <div className="bg-white">
          <div className="px-6 py-8 space-y-2">
            <div className="flex items-center justify-between">
              <div className="space-y-1 sm:space-y-2 min-w-0 flex-1">
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                  Step 6: IR21 Processing
                </h1>
                <p className="text-xs sm:text-sm text-gray-500 truncate">
                  {workflow.employee.first_name} {workflow.employee.last_name} • {workflow.employee.staff_id}
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-sm text-gray-500">Step 6 of {workflow?.requires_ir21 ? 7 : 6}</span>
                <StatusBadge status="completed" label="Skipped - Not Required" showIcon />
              </div>
            </div>
          </div>
        </div>

        {/* Skip Notice */}
        <div className="bg-green-50 border border-green-200 rounded-2xl p-8">
          <div className="flex items-center mb-4">
            <CheckCircle className="w-8 h-8 text-green-600 mr-4" />
            <div>
              <h2 className="text-xl font-semibold text-green-800">IR21 Processing Not Required</h2>
              <p className="text-green-700">This employee is a Singapore citizen/PR and does not require IR21 processing.</p>
            </div>
          </div>

          <div className="flex justify-between mt-8">
            <button
              onClick={handlePrev}
              className="btn btn-outline"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous Step
            </button>

            <button
              onClick={handleNext}
              className="btn btn-primary"
            >
              Next Step
              <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  const steps = getOffboardingSteps(workflow, workflowId);
  const progressPercentage = calculateProgressPercentage(workflow);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-white">
        <div className="px-6 py-8 space-y-2">
          <div className="flex items-center justify-between">
            <div className="space-y-1 sm:space-y-2 min-w-0 flex-1">
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                Step 6: IR21 Processing
              </h1>
              <p className="text-sm text-gray-500">
                {workflow.employee.first_name} {workflow.employee.last_name} • {workflow.employee.staff_id}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-500">Step 6 of {workflow?.requires_ir21 ? 7 : 6}</span>
              {workflow.step_6_completed && (
                <StatusBadge status="completed" showIcon />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Horizontal Stepper */}
      <HorizontalStepper
        steps={steps}
        currentStep={6}
        onStepClick={handleStepClick}
        progressPercentage={progressPercentage}
      />

      {/* Main Content */}
      <div className="px-6 py-8 space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* IR21 Form Processing */}
        <div className="bg-white rounded-2xl shadow-sm border p-8">
          <div className="flex items-center mb-4 sm:mb-6 min-w-0">
            <FileCheck className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 mr-2 sm:mr-3 flex-shrink-0" />
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">IR21 Form Processing</h2>
          </div>

          <div className="space-y-6">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 mr-2" />
                <span className="text-sm font-medium text-amber-800">Immigration Compliance Required</span>
              </div>
              <p className="text-sm text-amber-700">
                This employee requires IR21 processing as they are a foreign worker.
              </p>
            </div>

            <div>
              <label className="flex items-center mb-4">
                <input
                  type="checkbox"
                  checked={stepData.ir21_form_generated}
                  onChange={(e) => setStepData({...stepData, ir21_form_generated: e.target.checked})}
                  className="rounded border-gray-300 text-blue-600 focus-visible:ring-blue-500"
                  disabled={workflow.step_6_completed}
                />
                <span className="ml-2 text-sm font-medium text-gray-700">IR21 Form Generated</span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                IR21 Submitted Date
              </label>
              <input
                type="date"
                value={stepData.ir21_submitted_date}
                onChange={(e) => setStepData({...stepData, ir21_submitted_date: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                disabled={workflow.step_6_completed}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                IR21 Reference Number
              </label>
              <input
                type="text"
                value={stepData.ir21_reference_number}
                onChange={(e) => setStepData({...stepData, ir21_reference_number: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                placeholder="Enter reference number"
                disabled={workflow.step_6_completed}
              />
            </div>

            <div>
              <label className="flex items-center mb-2">
                <input
                  type="checkbox"
                  checked={stepData.work_permit_cancelled}
                  onChange={(e) => setStepData({...stepData, work_permit_cancelled: e.target.checked})}
                  className="rounded border-gray-300 text-blue-600 focus-visible:ring-blue-500"
                  disabled={workflow.step_6_completed}
                />
                <span className="ml-2 text-sm font-medium text-gray-700">Work Permit Cancelled</span>
              </label>
            </div>

            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={stepData.exit_permit_arranged}
                  onChange={(e) => setStepData({...stepData, exit_permit_arranged: e.target.checked})}
                  className="rounded border-gray-300 text-blue-600 focus-visible:ring-blue-500"
                  disabled={workflow.step_6_completed}
                />
                <span className="ml-2 text-sm font-medium text-gray-700">Exit Permit Arranged</span>
              </label>
            </div>
          </div>
        </div>

        {/* Compliance Checklist */}
        <div className="bg-white rounded-2xl shadow-sm border p-8">
          <div className="flex items-center mb-4 sm:mb-6 min-w-0">
            <Globe className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 mr-2 sm:mr-3 flex-shrink-0" />
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">Compliance Checklist</h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={stepData.compliance_checklist.work_permit_verified}
                  onChange={(e) => handleChecklistChange('work_permit_verified', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus-visible:ring-blue-500"
                  disabled={workflow.step_6_completed}
                />
                <div className="ml-3">
                  <div className="text-sm font-medium text-gray-900">Work Permit Verified</div>
                  <div className="text-xs text-gray-500">Original work permit validated</div>
                </div>
              </div>
              {stepData.compliance_checklist.work_permit_verified && (
                <CheckCircle className="w-5 h-5 text-green-600" />
              )}
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={stepData.compliance_checklist.exit_permit_applied}
                  onChange={(e) => handleChecklistChange('exit_permit_applied', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus-visible:ring-blue-500"
                  disabled={workflow.step_6_completed}
                />
                <div className="ml-3">
                  <div className="text-sm font-medium text-gray-900">Exit Permit Applied</div>
                  <div className="text-xs text-gray-500">Exit permit application submitted</div>
                </div>
              </div>
              {stepData.compliance_checklist.exit_permit_applied && (
                <CheckCircle className="w-5 h-5 text-green-600" />
              )}
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={stepData.compliance_checklist.embassy_notified}
                  onChange={(e) => handleChecklistChange('embassy_notified', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus-visible:ring-blue-500"
                  disabled={workflow.step_6_completed}
                />
                <div className="ml-3">
                  <div className="text-sm font-medium text-gray-900">Embassy Notified</div>
                  <div className="text-xs text-gray-500">Employee's embassy notified of departure</div>
                </div>
              </div>
              {stepData.compliance_checklist.embassy_notified && (
                <CheckCircle className="w-5 h-5 text-green-600" />
              )}
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={stepData.compliance_checklist.documents_collected}
                  onChange={(e) => handleChecklistChange('documents_collected', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus-visible:ring-blue-500"
                  disabled={workflow.step_6_completed}
                />
                <div className="ml-3">
                  <div className="text-sm font-medium text-gray-900">Documents Collected</div>
                  <div className="text-xs text-gray-500">All required immigration documents collected</div>
                </div>
              </div>
              {stepData.compliance_checklist.documents_collected && (
                <CheckCircle className="w-5 h-5 text-green-600" />
              )}
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={stepData.compliance_checklist.final_clearance_obtained}
                  onChange={(e) => handleChecklistChange('final_clearance_obtained', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus-visible:ring-blue-500"
                  disabled={workflow.step_6_completed}
                />
                <div className="ml-3">
                  <div className="text-sm font-medium text-gray-900">Final Clearance Obtained</div>
                  <div className="text-xs text-gray-500">Immigration final clearance received</div>
                </div>
              </div>
              {stepData.compliance_checklist.final_clearance_obtained && (
                <CheckCircle className="w-5 h-5 text-green-600" />
              )}
            </div>
          </div>

          {isChecklistComplete() && (
            <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center text-green-800">
                <CheckCircle className="w-5 h-5 mr-2" />
                <span className="text-sm font-medium">All compliance requirements completed</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Notes Section */}
      <div className="bg-white rounded-2xl shadow-sm border p-8">
        <div className="flex items-center mb-4 sm:mb-6 min-w-0">
          <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 mr-2 sm:mr-3 flex-shrink-0" />
          <h2 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">Immigration Processing Notes</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Processing Timeline
            </label>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• IR21 Form Submission: 1-2 weeks</li>
                <li>• Work Permit Cancellation: 2-4 weeks</li>
                <li>• Exit Permit Processing: 1-3 weeks</li>
                <li>• Final Clearance: 1-2 weeks</li>
              </ul>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional Notes
            </label>
            <textarea
              value={stepData.immigration_notes}
              onChange={(e) => setStepData({...stepData, immigration_notes: e.target.value})}
              rows={6}
              className="textarea-admin"
              placeholder="Any additional immigration processing notes, special requirements, or follow-up actions..."
              disabled={workflow.step_6_completed}
            />
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={handlePrev}
          className="btn btn-admin-outline"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Previous Step
        </button>

        <div className="flex space-x-3">
          {!workflow.step_6_completed && workflow.workflow_status !== 'withdrawn' ? (
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn btn-admin-secondary"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Progress'}
            </button>
          ) : null}

          {!workflow.step_6_completed && workflow.workflow_status !== 'withdrawn' && (
            <button
              onClick={() => setShowWithdrawalModal(true)}
              className="btn btn-danger"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Withdraw
            </button>
          )}

          <button
            onClick={handleNext}
            className="btn btn-admin-primary"
          >
            Next Step
            <ArrowRight className="w-4 h-4 ml-2" />
          </button>
        </div>

        {/* Withdrawal Modal */}
        <WithdrawalModal
          isOpen={showWithdrawalModal}
          onClose={() => setShowWithdrawalModal(false)}
          currentStep={6}
          onConfirmWithdraw={handleWithdrawal}
          workflow={workflow}
        />
        </div>
      </div>
    </div>
  );
};

export default OffboardingStep6;
